import React, { useMemo } from 'react';
import { useCustomFields, CustomField } from '../../custom-fields/useData';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';

export interface CustomFieldUsageStatsProps {
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const CustomFieldUsageStats: React.FC<CustomFieldUsageStatsProps> = ({
  embedded,
}) => {
  const { data: fields, isLoading } = useCustomFields();
  const { colors } = useTheme();
  const navigate = useNavigate();

  const byEntity = useMemo(() => {
    const arr = Array.isArray(fields) ? fields : [];
    const map = new Map<string, number>();
    arr.forEach((f: CustomField) => {
      const type = f.entityType || 'Other';
      map.set(type, (map.get(type) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([entityType, count]) => ({ entityType, count }))
      .sort((a, b) => b.count - a.count);
  }, [fields]);

  const cardClass = embedded
    ? 'min-w-0'
    : `${colors.surface} rounded-xl shadow-sm p-5 ${colors.border} border`;

  if (isLoading) {
    return (
      <div className={cardClass}>
        <h2 className={`text-base font-semibold ${colors.text} mb-3`}>
          Custom Fields
        </h2>
        <div className="animate-pulse h-24 rounded-lg bg-gray-100 dark:bg-gray-700" />
      </div>
    );
  }

  const total = (Array.isArray(fields) ? fields : []).length;

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <h2 className={`text-base font-semibold ${colors.text}`}>
          Custom Fields
        </h2>
        <button
          type="button"
          onClick={() => navigate('/custom-fields')}
          className="text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400 font-medium"
        >
          Manage
        </button>
      </div>
      <p className={`text-2xl font-bold ${colors.text} mb-3`}>{total}</p>
      <p className={`text-xs ${colors.textMuted} mb-3`}>Total fields</p>
      <div className="space-y-1.5">
        {byEntity.slice(0, 6).map(({ entityType, count }) => (
          <div
            key={entityType}
            className={`flex justify-between text-sm ${colors.text}`}
          >
            <span>{entityType}</span>
            <span className={colors.textMuted}>{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CustomFieldUsageStats;
